/**
 * Esquemas Zod compartidos por todos los contratos de entrada: identificadores,
 * fechas, montos, documentos guatemaltecos, texto libre y parámetros de
 * listado.
 */
import { z } from 'zod';
import { isIsoDate } from '../calendar/iso-date.js';
import { keyedEnum } from '../catalogs/enum.js';
import { DEPARTMENT_CODES } from '../identity/geography.js';
import { isValidCui, isValidNit, normalizeCui, normalizeGuatemalanPhone, normalizeNit, } from '../identity/documents.js';
/**
 * Longitudes máximas de texto libre, agrupadas por uso para que los
 * formularios, la API y la base de datos acepten lo mismo.
 */
export const TEXT_MAX = {
    /** Números de boleta, transferencia, cheque, actas y referencias cortas. */
    reference: 100,
    /** Etiquetas cortas: municipio, parentesco. */
    label: 100,
    /** Nombres o apellidos por separado, nombres de producto o agencia, correo. */
    personName: 120,
    /** Nombre completo, razón social, ocupación. */
    name: 200,
    /** Descripciones y observaciones breves, direcciones. */
    shortText: 500,
    /** Notas de operación. */
    notes: 1000,
    /** Motivos, condiciones y notas extensas. */
    longText: 2000,
    /** Justificaciones y observaciones de análisis de crédito. */
    narrative: 4000,
};
/** Longitud mínima de un motivo (reversa, rechazo, castigo): obliga a explicar algo útil. */
export const REASON_MIN_LENGTH = 10;
/** Tope de una tasa en puntos básicos: 1000 % (las tasas moratorias pueden superar 100 %). */
export const MAX_BASIS_POINTS = 100_000;
/** Límites geográficos válidos de latitud y longitud (grados). */
const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;
/** Página por defecto de los listados (base 1). */
export const DEFAULT_PAGE = 1;
/** Tamaño de página por defecto de los listados. */
export const DEFAULT_PAGE_SIZE = 20;
/** Tamaño de página máximo que acepta la API (protege a la base de datos). */
export const MAX_PAGE_SIZE = 100;
/** Longitud máxima del texto de búsqueda de un listado. */
const MAX_SEARCH_LENGTH = 100;
/** Longitud máxima del nombre de columna de orden (se valida luego contra un whitelist). */
const MAX_SORT_KEY_LENGTH = 40;
/** Direcciones de orden de los listados. */
export const SORT_DIRECTIONS = ['asc', 'desc'];
/** Catálogo con clave de direcciones de orden: `SORT_DIRECTION.desc`. */
export const SORT_DIRECTION = keyedEnum(SORT_DIRECTIONS);
/** Identificador UUID. */
export const idSchema = z.string().uuid('Identificador inválido');
/**
 * Identificador que el dispositivo genera para una operación que puede
 * capturarse sin conexión y enviarse más tarde (o reenviarse si la señal se
 * corta a medio envío). Si el servidor ya registró una operación con el mismo
 * identificador, devuelve la original en lugar de duplicarla.
 */
export const clientRequestIdSchema = idSchema.optional();
/**
 * Días máximos que una operación puede esperar en el teléfono antes de
 * enviarse. Coincide con la vigencia del paquete de campo (3 días): más allá,
 * los saldos con que se negoció ya no son confiables y la captura se
 * considera dudosa (reloj mal puesto, dato olvidado o intento de antedatar),
 * así que el servidor la rechaza y hay que registrarla de nuevo.
 */
export const OFFLINE_CAPTURE_MAX_DAYS = 3;
/** Momento (ISO 8601 con zona) en que se capturó una operación en el teléfono. */
export const capturedAtSchema = z.string().datetime({ offset: true, message: 'Fecha y hora de captura inválida' }).optional();
/**
 * La hora de captura solo acompaña a lo guardado sin conexión (que siempre
 * trae `clientRequestId`). En línea manda el reloj del servidor: así un
 * equipo con la hora mal puesta no altera las fechas de negocio.
 */
export const CAPTURED_AT_REQUIRES_REQUEST_ID = 'La hora de captura solo aplica a operaciones guardadas sin conexión';
/** Fecha de calendario `YYYY-MM-DD`. */
export const isoDateSchema = z
    .string()
    .refine((value) => isIsoDate(value), 'Fecha inválida (formato AAAA-MM-DD)');
/** Monto no negativo en centavos. */
export const moneySchema = z
    .number({ invalid_type_error: 'El monto debe ser numérico' })
    .int('El monto debe expresarse en centavos (entero)')
    .nonnegative('El monto no puede ser negativo')
    .max(Number.MAX_SAFE_INTEGER);
/** Monto estrictamente positivo en centavos. */
export const positiveMoneySchema = moneySchema.positive('El monto debe ser mayor a cero');
/** Tasa en puntos básicos: 0 – 1000 % (las tasas moratorias pueden superar 100 %). */
export const basisPointsSchema = z.number().int().min(0).max(MAX_BASIS_POINTS);
/** DPI (CUI) normalizado y validado con dígito verificador, departamento y municipio. */
export const cuiSchema = z
    .string()
    .transform(normalizeCui)
    .refine(isValidCui, 'DPI (CUI) inválido: revisa los 13 dígitos, el departamento y el municipio');
/** NIT normalizado y validado con su dígito verificador (acepta "CF"). */
export const nitSchema = z.string().transform(normalizeNit).refine(isValidNit, 'NIT inválido');
/** Teléfono guatemalteco de 8 dígitos, normalizado sin prefijo ni separadores. */
export const phoneSchema = z.string().transform((value, ctx) => {
    const phone = normalizeGuatemalanPhone(value);
    if (!phone) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Teléfono inválido: 8 dígitos de Guatemala' });
        return z.NEVER;
    }
    return phone;
});
/** Código de departamento (01–22). */
export const departmentCodeSchema = z.enum(DEPARTMENT_CODES);
/** Coordenadas GPS capturadas en campo. */
export const coordinatesSchema = z.object({
    latitude: z.number().min(-MAX_LATITUDE).max(MAX_LATITUDE),
    longitude: z.number().min(-MAX_LONGITUDE).max(MAX_LONGITUDE),
});
/** Texto libre opcional: las cadenas vacías se convierten en `undefined`. */
export const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value === '' ? undefined : value));
/** Query string de todo endpoint de listado: `?page=&pageSize=&q=&sortBy=&sortDir=`. */
export const listQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(DEFAULT_PAGE),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    q: z.string().trim().max(MAX_SEARCH_LENGTH).optional(),
    sortBy: z.string().trim().max(MAX_SORT_KEY_LENGTH).optional(),
    sortDir: z.enum(SORT_DIRECTIONS).default(SORT_DIRECTION.desc),
});
/** Rango de fechas opcional para filtros de reportes y listados. */
export const dateRangeQuerySchema = z.object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
//# sourceMappingURL=common.js.map